<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
import {useNow} from "@vueuse/core";
import {ChevronLeft, ChevronRight, Loader2} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import useAgenda from "@/agenda/composables/useAgenda";
import useOperatorChoices from "@/agenda/composables/useOperatorChoices";
import useFrequentation from "@/agenda/composables/useFrequentation";
import {DAY_END_HOUR, DAY_START_HOUR, SLOT_MINUTES} from "@/agenda/constants";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {colorForLevel, frequentationSlot, labelForLevel, textForLevel} from "@/agenda/lib/frequentation";
import {colors} from "@/theme/colors";

/**
 * The slots of one day of the agenda, for one collaborator, to hold an act
 * of a given length.
 *
 * Deliberately permissive: it shows what is taken and who is habilitated,
 * and lets the pharmacist decide anyway, because they know things the
 * agenda does not. The day and the collaborator belong to the caller, which
 * decides where the grid opens and when it starts again.
 */
const props = defineProps<{
    appointmentTypeId: number | null;
    durationMinutes: number;
    /** Whether the agenda is read: off while the grid is not on screen. */
    active: boolean;
    /** The slot already chosen on the day shown, highlighted. */
    selected?: string | null;
    /** The appointment being moved, which does not hold its own slots. */
    excludedAppointmentId?: number | null;
    /** Whether the collaborator is set by the caller and cannot be changed. */
    memberLocked?: boolean;
}>();

const emits = defineEmits<{
    (e: 'pick', moment: {date: string; start: string; member_id: number | null}): void;
}>();

const day = defineModel<string>('day', {required: true});
const memberId = defineModel<number | null>('member', {required: true});

/**
 * Followed rather than captured: a counter workstation stays on this screen
 * all day and through the night, and a « today » frozen at mount would let
 * yesterday be picked and keep grading this morning against yesterday's clock.
 */
const now = useNow({interval: 60 * 1000});
const today = computed(() => dayjs(now.value).startOf('day'));

const dayStart = computed(() => parseAgendaDate(day.value).startOf('day'));

/**
 * A native date field can be emptied, and it then holds `''`. Everything
 * downstream would carry an Invalid Date: the grid would render undated slots
 * that still look pickable, and the pick would leave with no date at all, to
 * be refused by the server as a validation error nobody could act on.
 */
const dayUsable = computed(() => /^\d{4}-\d{2}-\d{2}$/.test(day.value) && dayStart.value.isValid());

const range = computed(() => props.active && dayUsable.value
    ? {from: dayStart.value.toDate(), to: dayStart.value.endOf('day').toDate()}
    : null);

const {data: appointments, isLoading: agendaLoading} = useAgenda(range);
const {frequentation} = useFrequentation(() => props.active && dayUsable.value ? dayStart.value.startOf('week') : null);

/**
 * The whole team in its order for this act: the pharmacist may hand the act
 * to anybody, those habilitated for it come first.
 */
const {choices} = useOperatorChoices(() => ({appointmentTypeId: props.appointmentTypeId}));

const minutesOf = (date: string) => {
    const moment = parseAgendaDate(date);

    return moment.hour() * 60 + moment.minute();
};

/**
 * What the chosen collaborator already holds that day.
 *
 * Only theirs: an appointment nobody is assigned to conflicts with nobody in
 * particular, so picking « sans collaborateur » must not grey out a whole
 * busy day the pharmacist may legitimately book into.
 */
const held = computed(() => memberId.value === null
    ? []
    : (appointments.value ?? []).filter((appointment) =>
        appointment.status !== 'canceled'
        && appointment.status !== 'refused'
        && appointment.id !== props.excludedAppointmentId
        && appointment.operator_access_point_id === memberId.value));

const isPast = computed(() => dayUsable.value && dayStart.value.isBefore(today.value, 'day'));

/** Minutes already elapsed today, past which nothing can be booked. */
const elapsed = computed(() => {
    const current = dayjs(now.value);

    return dayStart.value.isSame(current, 'day') ? current.hour() * 60 + current.minute() : -1;
});

/** The day's slots, from the grid's own bounds and step. */
const slots = computed(() => {
    const entries: {minutes: number; label: string; level: number | null; busy: boolean; past: boolean}[] = [];

    for (let minutes = DAY_START_HOUR * 60; minutes < DAY_END_HOUR * 60; minutes += SLOT_MINUTES) {
        const end = minutes + props.durationMinutes;
        const hour = Math.floor(minutes / 60);

        entries.push({
            minutes,
            label: `${String(hour).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
            level: frequentationSlot(frequentation.value, dayStart.value, hour)?.level ?? null,
            busy: held.value.some((appointment) =>
                minutesOf(appointment.start_at) < end && minutes < minutesOf(appointment.end_at)),
            past: isPast.value || minutes < elapsed.value
        });
    }

    return entries;
});

/**
 * The colours of a slot, set inline: the reset of a `type="button"` comes
 * after the utility classes and would take their text colour back. The
 * chosen slot reads in the colours of the brand, a free one in those of its
 * frequentation.
 */
const slotStyle = (slot: {label: string; level: number | null; busy: boolean; past: boolean}) => {
    if (slot.label === props.selected) {
        return {backgroundColor: colors.primary.DEFAULT, color: '#ffffff'};
    }

    return slot.busy || slot.past || slot.level === null
        ? undefined
        : {backgroundColor: colorForLevel(slot.level), color: textForLevel(slot.level)};
};

const shiftDay = (direction: number) => {
    const from = dayUsable.value ? dayStart.value : today.value;

    day.value = from.add(direction, 'day').format('YYYY-MM-DD');
};

const choose = (start: string) => {
    if (!dayUsable.value) {
        return;
    }

    emits('pick', {date: day.value, start, member_id: memberId.value});
};
</script>

<template>
  <div>
    <div class="mb-2 flex flex-wrap items-center gap-2">
      <Button variant="ghost" size="icon" aria-label="Jour précédent" @click="shiftDay(-1)">
        <ChevronLeft class="size-4" />
      </Button>
      <Input v-model="day" type="date" class="w-40" />
      <Button variant="ghost" size="icon" aria-label="Jour suivant" @click="shiftDay(1)">
        <ChevronRight class="size-4" />
      </Button>
      <select
        v-model="memberId"
        class="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm disabled:opacity-50"
        aria-label="Collaborateur"
        :disabled="memberLocked"
      >
        <option :value="null">
          Sans collaborateur
        </option>
        <option
          v-for="choice in choices"
          :key="choice.operator.accessId"
          :value="choice.operator.accessId"
        >
          {{ choice.operator.name }}{{ choice.habilitated ? ' — habilité' : '' }}
        </option>
      </select>
    </div>

    <p v-if="!dayUsable" class="mb-2 text-xs text-gray-600">
      Choisissez une date pour voir les créneaux de la journée.
    </p>
    <p v-else-if="isPast" class="mb-2 text-xs text-gray-600">
      Ce jour est passé : un rendez-vous ne peut être pris qu'à partir d'aujourd'hui.
    </p>

    <div v-if="agendaLoading" class="flex items-center gap-2 py-6 text-sm text-gray-500">
      <Loader2 class="size-4 animate-spin" />
      Lecture de l'agenda…
    </div>

    <div v-else class="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
      <button
        v-for="slot in slots"
        :key="slot.minutes"
        type="button"
        class="rounded-md border px-1 py-1.5 text-xs font-medium tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        :class="slot.label === selected
          ? 'border-primary'
          : slot.busy
            ? 'border-slate-200 bg-slate-100 text-gray-400 line-through'
            : 'border-slate-200 hover:border-primary'"
        :style="slotStyle(slot)"
        :disabled="!dayUsable || slot.past || slot.busy"
        :title="slot.busy
          ? 'Déjà pris'
          : slot.level === null ? undefined : labelForLevel(slot.level)"
        @click="choose(slot.label)"
      >
        {{ slot.label }}
      </button>
    </div>
  </div>
</template>
